import React, { useEffect, useState } from 'react';
import {
  collectionGroup,
  collection,
  query,
  getDocs,
  addDoc,
  orderBy,
  serverTimestamp,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../../firebase/firestore';
import EmojiPicker from 'emoji-picker-react';

const AdminSupport = () => {
  const [users, setUsers] = useState([]);
  const [userStatuses, setUserStatuses] = useState({});
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [showEmoji, setShowEmoji] = useState(false);

  // Load users and determine who has new messages
  useEffect(() => {
    const loadUsers = async () => {
      const snapshot = await getDocs(collectionGroup(db, 'messages'));
      const userIds = Array.from(new Set(snapshot.docs.map(doc => doc.ref.parent.parent.id)));
      setUsers(userIds);

      const statuses = {};

      // Get last message for each user
      await Promise.all(
        userIds.map(async userId => {
          const messagesRef = collection(db, `supportMessages/${userId}/messages`);
          const q = query(messagesRef, orderBy('createdAt', 'desc'));
          const docs = await getDocs(q);
          const last = docs.docs[0]?.data();

          statuses[userId] = last?.from === 'user'; // true = new message from user
        })
      );

      setUserStatuses(statuses);
    };

    loadUsers();
  }, []);

  // Subscribe to selected user's messages
  useEffect(() => {
    if (!selectedUserId) return;

    const messagesQuery = query(
      collection(db, `supportMessages/${selectedUserId}/messages`),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(messagesQuery, snapshot => {
      const data = snapshot.docs.map(doc => doc.data());
      setMessages(data);

      // If last message is from admin, clear "new" status
      if (data.length > 0) {
        const last = data[data.length - 1];
        if (last.from === 'admin') {
          setUserStatuses(prev => ({
            ...prev,
            [selectedUserId]: false,
          }));
        }
      }
    });

    return () => unsubscribe();
  }, [selectedUserId]);

  const sendReply = async () => {
    if (!reply.trim()) return;

    await addDoc(collection(db, `supportMessages/${selectedUserId}/messages`), {
      message: reply,
      from: 'admin',
      createdAt: serverTimestamp(),
    });

    setReply('');
    setShowEmoji(false);
  };

  return (
    <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Users list */}
      <div className="bg-white p-4 rounded shadow h-[80vh] overflow-y-auto">
        <h2 className="font-semibold text-lg mb-2">Users</h2>
        {users.map(userId => {
          const hasNew = userStatuses[userId];
          const isSelected = selectedUserId === userId;

          return (
            <button
              key={userId}
              onClick={() => setSelectedUserId(userId)}
              className={`
                block w-full text-left py-2 px-3 rounded transition
                ${isSelected ? 'bg-blue-100' : hasNew ? 'bg-yellow-100' : 'hover:bg-gray-100'}
              `}
            >
              {userId}
              {hasNew && (
                <span className="ml-2 text-xs text-orange-500 font-semibold">New</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Messages and chat */}
      <div className="md:col-span-2 bg-white p-4 rounded shadow flex flex-col h-[80vh]">
        <div className="flex-1 overflow-y-auto space-y-3 mb-4">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`p-3 rounded-lg max-w-xs ${
                msg.from === 'admin' ? 'bg-blue-100 self-end' : 'bg-gray-100 self-start'
              }`}
            >
              <p className="text-sm text-black">{msg.message}</p>
              <p className="text-xs text-gray-800 text-right">
                {msg.from === 'admin' ? 'You' : msg.fullName || msg.username || 'User'}
              </p>
            </div>
          ))}
        </div>

        {/* Reply input */}
        <div className="flex flex-col">
          <textarea
            className="border rounded p-2 resize-none min-h-[80px]"
            placeholder="Reply to user..."
            value={reply}
            onChange={e => setReply(e.target.value)}
          />

          <div className="flex items-center justify-between mt-2">
            <button
              className="text-blue-500 text-sm hover:underline"
              onClick={() => setShowEmoji(prev => !prev)}
            >
              😊 Add emoji
            </button>
            <button
              onClick={sendReply}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Send
            </button>
          </div>

          {showEmoji && (
            <div className="mt-2">
              <EmojiPicker
                onEmojiClick={e => setReply(prev => prev + e.emoji)}
                height={300}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSupport;
